import React from 'react';
import PropTypes from 'prop-types';
import { Form } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import '../alert-form.css';
import FormErrorHandler
  from '@avtechsoftware/react-component-library/src/components/common/FormErrorHandler/FormErrorHandler';
import ErrorWrapper
  from '@avtechsoftware/react-component-library/src/components/common/FormErrorHandler/ErrorWrapper/ErrorWrapper';
import { OperatorTypeDetails, OperatorTypes } from '@avtechsoftware/ra-javascript-utilities/src/types/OperatorTypes';
import ReadingType, { findReadingTypeById } from '@avtechsoftware/ra-javascript-utilities/src/types/ReadingType';
import { selectAllThresholdRanges } from '../../../../redux/slices/thresholdRangeSlice/thresholdRangeSlice';
import {
  AlertFormSteps,
  selectSensorsData,
  selectThresholdData,
  updateStepData,
} from '../../../../redux/slices/alertFormSlice/alertFormSlice';
import { selectSessionInformation } from '../../../../redux/slices/sessionSlice';

function AlertFormSharedThresholdSelector({ disabled, onCreateNew }) {
  const dispatch = useDispatch();
  const thresholdList = useSelector(selectAllThresholdRanges);
  const selectedSensorsData = useSelector(selectSensorsData);
  const selectedThresholdRangeData = useSelector(selectThresholdData);
  const sessionInfo = useSelector(selectSessionInformation);

  const selectedThresholdRangeId = selectedThresholdRangeData.thresholdId
  && selectedThresholdRangeData.useSharedThreshold
    ? selectedThresholdRangeData.thresholdId : -1;

  const filteredThresholdList = thresholdList.filter(
    (threshold) => threshold.readingType === selectedSensorsData.readingType
          && (threshold.operatorId !== OperatorTypes.BETWEEN
            && threshold.operatorId !== OperatorTypes.NOT_BETWEEN),
  );

  const handleSelectChange = (e) => {
    const selectedValue = e.target.value;
    if (selectedValue === 'create-new') {
      onCreateNew();
    } else {
      const thresholdId = selectedValue ? Number(selectedValue) : null;
      if (selectedValue === '-1' || Number.isNaN(selectedValue)) return;
      dispatch(updateStepData({
        step: AlertFormSteps.DEFINE_THRESHOLD,
        data: { ...selectedThresholdRangeData, thresholdId, useSharedThreshold: true },
      }));
    }
  };

  return (
    <>
      <FormErrorHandler
        fieldList={['thresholdId']}
        errorList={selectedThresholdRangeData.errorList}
      >
        <div className="ml-3">
          <ErrorWrapper fieldId="thresholdId">
            <Form.Control
              className="w-auto"
              as="select"
              value={selectedThresholdRangeId || ''}
              onChange={handleSelectChange}
              disabled={disabled}
              defaultValue={-1}
            >
              <option disabled value={-1}>- Select a Threshold -</option>
              {filteredThresholdList.map((threshold) => {
                const thresholdOperator = threshold.operatorId ? threshold.operatorId : '';
                const operatorDetails = thresholdOperator
                  ? OperatorTypeDetails[thresholdOperator]
                  : null;
                const operatorDisplay = operatorDetails && operatorDetails.symbol
                  ? operatorDetails.symbol
                  : null;
                const readingType = findReadingTypeById(threshold.readingType);
                const detailsText = operatorDisplay
                  ? ` (${operatorDisplay} ${threshold.value} ${readingType.getUnits(sessionInfo)})`
                  : '';
                return (
                  <option key={threshold.id} value={threshold.id}>
                    {threshold.name}
                    {detailsText}
                  </option>
                );
              })}
              <option value="create-new">- Create New -</option>
            </Form.Control>
            {selectedSensorsData.readingType === ReadingType.GENERIC_BOOLEAN.id && (
              <span className="ml-3 mt-2 text-primary">
                <strong>
                  Shared Thresholds are not available
                  for two-state sensor readings (On/Off, Open/Closed, etc.)
                </strong>
              </span>
            )}
          </ErrorWrapper>
        </div>
      </FormErrorHandler>
    </>
  );
}

AlertFormSharedThresholdSelector.propTypes = {
  disabled: PropTypes.bool,
  onCreateNew: PropTypes.func.isRequired,
};

AlertFormSharedThresholdSelector.defaultProps = {
  disabled: false,
};

export default AlertFormSharedThresholdSelector;
